import { useState, useEffect, useRef, useCallback } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { LayoutDashboard, Package, ShoppingCart, Truck, BarChart3, Users, LogOut, Menu, X, ExternalLink, Settings, BookOpen, Star, KeyRound, ClipboardList, Volume2, VolumeX, Wallet, Receipt, Pill, Calculator, LifeBuoy, Bike, Clapperboard } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { ChangePasswordModal } from "./ChangePasswordModal";
import { api } from "../../lib/api";
import { toast } from "sonner";

const ROLE_LABELS = { patron: "Patron", vendeur: "Vendeur", associe: "Associé" };

function playBeep() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.type = "sine"; osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
    osc.start(); osc.stop(ctx.currentTime + 0.42);
  } catch { /* ignore */ }
}

export function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [newCount, setNewCount] = useState(0);
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("noumabou_sound") !== "off");
  const prevCount = useRef(null);
  const soundRef = useRef(soundOn);
  useEffect(() => { soundRef.current = soundOn; }, [soundOn]);

  const toggleSound = () => {
    setSoundOn((v) => {
      const next = !v;
      localStorage.setItem("noumabou_sound", next ? "on" : "off");
      if (next) playBeep();
      return next;
    });
  };

  const pollNewOrders = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/orders-new-count");
      const c = data.count || 0;
      if (prevCount.current !== null && c > prevCount.current) {
        if (soundRef.current) playBeep();
        toast.success("🛒 Nouvelle commande reçue !");
      }
      prevCount.current = c;
      setNewCount(c);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    pollNewOrders();
    const id = setInterval(pollNewOrders, 20000);
    return () => clearInterval(id);
  }, [pollNewOrders]);

  const nav = [
    { to: "/admin", label: "Tableau de bord", icon: LayoutDashboard, roles: ["patron", "vendeur", "associe"], end: true },
    { to: "/admin/commandes", label: "Ventes & commandes", icon: ShoppingCart, roles: ["patron", "vendeur", "associe"] },
    { to: "/admin/livraisons", label: "Livraisons", icon: Truck, roles: ["patron", "vendeur", "associe"] },
    { to: "/admin/livreurs", label: "Livreurs (règlement)", icon: Bike, roles: ["patron", "associe", "vendeur"] },
    { to: "/admin/studio", label: "Studio Pub", icon: Clapperboard, roles: ["patron", "associe", "vendeur"] },
    { to: "/admin/produits", label: "Stock & produits", icon: Package, roles: ["patron", "vendeur", "associe"] },
    { to: "/admin/pharmacies", label: "Dépôts pharmacie", icon: Pill, roles: ["patron", "vendeur", "associe"] },
    { to: "/admin/avis", label: "Avis clients", icon: Star, roles: ["patron", "associe"] },
    { to: "/admin/rapports", label: "Bénéfices & rapports", icon: BarChart3, roles: ["patron", "associe"] },
    { to: "/admin/comptabilite", label: "Comptabilité", icon: Calculator, roles: ["patron", "associe"] },
    { to: "/admin/depenses", label: "Dépenses", icon: Receipt, roles: ["patron", "associe"] },
    { to: "/admin/suivi", label: "Suivi du projet", icon: ClipboardList, roles: ["patron", "associe"] },
    { to: "/admin/partenaires", label: "Partenaires", icon: Wallet, roles: ["patron", "associe"] },
    { to: "/admin/ministere", label: "Ministère (dons)", icon: BookOpen, roles: ["patron"] },
    { to: "/admin/utilisateurs", label: "Utilisateurs", icon: Users, roles: ["patron"] },
    { to: "/admin/parametres", label: "Paiement & réglages", icon: Settings, roles: ["patron"] },
    { to: "/admin/mode-emploi", label: "Mode d'emploi", icon: LifeBuoy, roles: ["patron", "vendeur", "associe"] },
  ].filter((n) => n.roles.includes(user?.role));

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const SidebarContent = () => (
    <>
      <div className="flex items-center gap-2 px-4 h-16 border-b border-white/10">
        <span className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center">
          <img src="/noumabou-emblem.png" alt="" className="w-6 h-6 object-contain" />
        </span>
        <div>
          <p className="font-heading font-extrabold text-white leading-none">NOUMABOU</p>
          <p className="text-[11px] text-white/50">Gestion</p>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1">
        {nav.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={() => setOpen(false)}
            data-testid={`nav-${n.to.split("/").pop() || "dashboard"}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                isActive ? "bg-brand text-white" : "text-white/70 hover:bg-white/10 hover:text-white"
              }`
            }
          >
            <n.icon className="w-5 h-5" /> <span className="flex-1">{n.label}</span>
            {n.to === "/admin/commandes" && newCount > 0 && (
              <span data-testid="new-orders-badge" className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center">
                {newCount}
              </span>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="px-3 py-4 border-t border-white/10 space-y-1">
        <a href="/boutique" target="_blank" rel="noreferrer" className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-white/70 hover:bg-white/10 hover:text-white">
          <ExternalLink className="w-5 h-5" /> Voir la boutique
        </a>
        <button onClick={() => { setPwOpen(true); setOpen(false); }} data-testid="change-password-button" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-white/70 hover:bg-white/10 hover:text-white">
          <KeyRound className="w-5 h-5" /> Changer mon mot de passe
        </button>
        <button onClick={toggleSound} data-testid="sound-toggle-button" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-white/70 hover:bg-white/10 hover:text-white">
          {soundOn ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
          Son des alertes : <span className={`font-semibold ${soundOn ? "text-green-400" : "text-white/40"}`}>{soundOn ? "activé" : "coupé"}</span>
        </button>
        <button onClick={handleLogout} data-testid="logout-button" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-white/70 hover:bg-white/10 hover:text-white">
          <LogOut className="w-5 h-5" /> Déconnexion
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-brand-cream flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-64 bg-brand-ink fixed inset-y-0">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-64 bg-brand-ink flex flex-col"><SidebarContent /></div>
          <div className="flex-1 bg-black/40" onClick={() => setOpen(false)} />
        </div>
      )}

      <div className="flex-1 lg:ml-64">
        <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-brand-line h-16 flex items-center justify-between px-4 sm:px-6">
          <button className="lg:hidden flex items-center gap-2 px-3 py-2 rounded-full bg-brand-ink text-white text-sm font-semibold" onClick={() => setOpen(true)} data-testid="admin-menu-toggle"><Menu className="w-5 h-5" /> Menu</button>
          <div className="flex-1" />
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-sm font-semibold text-brand-ink leading-none" data-testid="current-user-name">{user?.name}</p>
              <p className="text-xs text-brand-muted">{ROLE_LABELS[user?.role] || user?.role}</p>
            </div>
            <span className="w-9 h-9 rounded-full bg-brand-accent flex items-center justify-center text-white font-semibold">
              {user?.name?.charAt(0)?.toUpperCase()}
            </span>
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
      {pwOpen && <ChangePasswordModal onClose={() => setPwOpen(false)} />}
    </div>
  );
}
